// Redesign 2026 – našeptávač hledání v blogu (hlavička výpisu /blog/ a rubrik).
// Markup: includes/template-parts/redesign/blog/hero.php, data: GET efi/v1/blog-search?q=…
// (includes/redesign/blog-search.php). Bez JS funguje formulář sám – odešle /blog/?hledat=….
//
//   [data-rd-blog-search]        formulář (data-endpoint, data-all-label, data-empty-label, data-rubriky-label)
//   [data-rd-blog-search-input]  pole (combobox)
//   [data-rd-blog-search-list]   seznam návrhů (listbox)

const MIN_CHARS = 2;
const DEBOUNCE_MS = 220;

export default function initRedesignBlogSearch() {
  const form = document.querySelector('[data-rd-blog-search]');
  if (!form) return;

  const input = form.querySelector('[data-rd-blog-search-input]');
  const list = form.querySelector('[data-rd-blog-search-list]');
  const endpoint = form.dataset.endpoint;
  if (!input || !list || !endpoint) return;

  const cache = new Map();
  let timer = null;
  let controller = null;
  let options = [];
  let active = -1;

  const close = () => {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  };

  const setActive = (index) => {
    options.forEach((el, i) => el.setAttribute('aria-selected', i === index ? 'true' : 'false'));
    active = index;
    if (index >= 0 && options[index]) {
      input.setAttribute('aria-activedescendant', options[index].id);
      options[index].scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  const render = (data) => {
    list.textContent = '';
    options = [];
    active = -1;

    // rubriky, jejichž název odpovídá výrazu – odkaz na archiv rubriky
    const rubriky = data.rubriky || [];
    if (rubriky.length) {
      const row = el('div', 'rd-blog__suggest-rubriky');
      row.append(el('span', 'rd-blog__suggest-label', form.dataset.rubrikyLabel || ''));
      rubriky.forEach((rubrika, i) => {
        const link = el('a', 'rd-blog__suggest-rubrika', rubrika.label);
        link.href = rubrika.url;
        link.id = `rd-blog-naseptavac-r${i}`;
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');
        link.tabIndex = -1;
        row.append(link);
        options.push(link);
      });
      list.append(row);
    }

    if (!data.items.length && !rubriky.length) {
      list.append(el('p', 'rd-blog__suggest-empty', form.dataset.emptyLabel || ''));
    }

    data.items.forEach((item, i) => {
      const link = el('a', 'rd-blog__suggest-item');
      link.href = item.url;
      link.id = `rd-blog-naseptavac-${i}`;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.tabIndex = -1;

      if (item.thumb) {
        const img = el('img', 'rd-blog__suggest-thumb');
        img.src = item.thumb;
        img.alt = '';
        img.width = 56;
        img.height = 56;
        img.loading = 'lazy';
        link.append(img);
      } else {
        link.append(el('span', 'rd-blog__suggest-thumb rd-blog__suggest-thumb--empty'));
      }

      const body = el('span', 'rd-blog__suggest-body');
      body.append(el('span', 'rd-blog__suggest-title', item.title));
      body.append(el('span', 'rd-blog__suggest-meta', [item.date, item.rubrika].filter(Boolean).join(' · ')));
      link.append(body);

      list.append(link);
      options.push(link);
    });

    if (data.total > data.items.length && data.url) {
      const all = el('a', 'rd-blog__suggest-all', `${form.dataset.allLabel || ''} (${data.total})`);
      all.href = data.url;
      all.id = 'rd-blog-naseptavac-all';
      all.setAttribute('role', 'option');
      all.setAttribute('aria-selected', 'false');
      all.tabIndex = -1;
      list.append(all);
      options.push(all);
    }

    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const search = async (term) => {
    if (cache.has(term)) {
      render(cache.get(term));
      return;
    }

    if (controller) controller.abort();
    controller = new AbortController();

    try {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', term);
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      cache.set(term, data);
      // odpověď na starší výraz, mezitím uživatel psal dál
      if (input.value.trim() === term) render(data);
    } catch (error) {
      if (error.name !== 'AbortError') close();
    }
  };

  input.addEventListener('input', () => {
    clearTimeout(timer);
    const term = input.value.trim();
    if (term.length < MIN_CHARS) {
      if (controller) controller.abort();
      close();
      return;
    }
    timer = setTimeout(() => search(term), DEBOUNCE_MS);
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (!list.hidden) {
        event.preventDefault();
        close();
      }
      return;
    }

    if (list.hidden || !options.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(active + 1 >= options.length ? 0 : active + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(active - 1 < 0 ? options.length - 1 : active - 1);
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      window.location.href = options[active].href;
    }
  });

  input.addEventListener('focus', () => {
    if (list.childElementCount && input.value.trim().length >= MIN_CHARS) {
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }
  });

  document.addEventListener('click', (event) => {
    if (!form.contains(event.target)) close();
  });

  form.addEventListener('submit', (event) => {
    if (input.value.trim() === '') event.preventDefault();
  });
}
